import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { articleContents } from '@/mocks/articles';

export default function ArticleTemplate() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articleContents.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEOMeta type="homepage" pageTitle="Page Not Found | oopbuy.boutique" />
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="w-full px-4 md:px-6 lg:px-12">
            <div className="max-w-3xl mx-auto text-center py-20">
              <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article Not Found</h1>
              <p className="text-sm text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
              <Link to="/" className="px-5 py-2 bg-primary-500 text-background-50 text-xs font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer">
                Back to Homepage
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        type="article"
        articleData={{
          title: article.title,
          description: article.cardDescription,
          slug: `article/${article.slug}`,
          datePublished: '2026-06-15',
          imageUrl: article.cardImage,
        }}
      />
      <Navbar />
      <main className="pt-20 pb-12">
        <article className="w-full px-4 md:px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <nav className="mb-8">
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Back to Home
              </Link>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-md mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm text-foreground-500">
                Last updated: June 2026 &bull; {article.readTime} &bull; By OOPBuy Editorial Team
              </p>
            </header>

            <section className="prose-custom">
              {article.intro.map((p, i) => (
                <p key={i} className="text-base text-foreground-700 leading-relaxed mb-6">
                  {p}
                </p>
              ))}

              {article.sections.map((section, si) => {
                if (section.headingType === 'h2') {
                  return (
                    <div key={si}>
                      <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-5">
                        {section.heading}
                      </h2>
                      {section.paragraphs.map((p, pi) => (
                        <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-6">
                          {p}
                        </p>
                      ))}
                    </div>
                  );
                }
                return (
                  <div key={si}>
                    <h3 className="text-lg font-semibold text-foreground-950 mt-8 mb-3">
                      {section.heading}
                    </h3>
                    {section.paragraphs.map((p, pi) => (
                      <p key={pi} className="text-base text-foreground-700 leading-relaxed mb-6">
                        {p}
                      </p>
                    ))}
                  </div>
                );
              })}

              {article.comparisonTable && (
                <div className="overflow-x-auto my-8">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-background-300">
                        {article.comparisonTable.headers.map((h, i) => (
                          <th key={i} className={`text-left py-3 pr-4 text-xs font-semibold uppercase tracking-wider ${i === 0 ? 'text-foreground-500' : i === 1 ? 'text-accent-700' : 'text-foreground-400'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {article.comparisonTable.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-background-200 last:border-b-0">
                          {row.map((cell, ci) => (
                            <td key={ci} className={`py-3 pr-4 ${ci === 0 ? 'text-foreground-800 font-medium' : ci === 1 ? 'text-accent-700 font-medium' : 'text-foreground-500'}`}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-5">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4 my-8">
                {article.faqs.map((faq) => (
                  <div key={faq.question} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">
                      {faq.question}
                    </h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 bg-accent-100/50 rounded-lg border border-accent-200/60">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                  Ready to explore the OOPBuy Spreadsheet system?
                </h3>
                <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                  Visit the main platform to access structured product data across 14 categories, or browse our other SEO guides for deeper insights into spreadsheet-based sourcing methodology.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-5 py-2 bg-primary-500 text-background-50 text-xs font-semibold rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Open OOPBuy Spreadsheet
                  </a>
                  <Link
                    to="/"
                    className="px-5 py-2 bg-background-50 text-foreground-700 text-xs font-semibold rounded-md border border-background-200 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                  </Link>
                </div>
              </div>
            </section>

            <nav className="mt-14 pt-8 border-t border-background-200">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Return to Homepage
              </Link>
            </nav>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}